import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getTime, scrollToBottom } from "./ChatHandler";
import { Message } from "./ChatInterface";
import {
  fetchCategories,
  generateTemplate,
  fetchCampaignListsAndCompany,
} from "src/services/EngagementAPIs";
import {
  fetchChatMessages,
  getChatImageUrl,
  fetchChatImage,
  getActiveChatSessionId,
  resetChatSession,
  setActiveChatSessionId,
  syncChatbotMessageNumber,
} from "src/services/ChatbotAPI";
import { getApiOriginUrl } from "src/config/endpoints";
import { useNavigate, useLocation } from "react-router-dom";
import { Avatar, message } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { autoCorrectInput } from "src/utils/corrections";
import { Category } from "../../components/CategoryStep";
import ChatInput from "./ChatInput";
import "./ChatScreen.scss";
import { ChatHistorySidebar, ChatSession } from "./ChatHistorySidebar";
import { campaigns, subCampaigns, CampaignKey } from "src/utils/constants";
import { translateCampaignLabel } from "src/utils/campaignLabels";
import {
  handleQuickSchedule,
  handleConfirmSchedule,
  handleCancelSchedule,
  handleScheduleConfirmation,
} from "src/utils/ScheduleUtils";
import { createMessageHandler } from "src/handlers/messageHandlers";
import { PromptStep } from "src/components/PromptStep";
import { CustomCampaignForm } from "src/components/CustomCampaignForm";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  audienceListMatchesSearch,
  CREATE_NEW_LIST_NAME,
  registerAudienceListLabels,
  toAudienceListOptions,
  type AudienceListOption,
} from "src/utils/audienceListLabels";
import { Volume2, ImagePlus, ChartLine, CircleDollarSign, ListPlus, Search } from "lucide-react";

const API_ORIGIN = getApiOriginUrl();

const chatScreenTranslationKeys = [
  "unableToLoadSession",
  "missingApiCredentials",
  "createNewList",
  "noCategoriesDetected",
  "failedToFetchCategories",
  "roasReply",
  "conversionsReply",
  "impressionsReply",
  "engagementReply",
  "createCampaignReply",
  "templateGenerationError",
  "failedToGenerateTemplate",
  "noCategoriesSelected",
  "selectedCategories",
  "selectCampaignCategory",
  "okayNoProblem",
  "showCampaignPerformance",
  "viewReports",
  "clearChat",
  "newChat",
  "toggleSidebar",
  "greetingTitle",
  "greetingSubtitle",
  "suggestionsLabel",
  "returnOnAdSpend",
  "engagementAndLikes",
  "createNewCampaign",
  "conversions",
  "impressions",
  "fetchedCategoriesPrompt",
  "searchCategoriesPlaceholder",
  "noCategoriesMatchSearch",
  "showMoreCategories",
  "proceed",
  "customCampaignCreated",
  "generatedTemplate",
  "previewTemplate",
  "readyToSend",
  "yes",
  "no",
  "edit",
  "sendOrSchedule",
  "alreadySentWarning",
  "alreadySentScheduleWarning",
  "sendNow",
  "schedule",
  "scheduleCampaign",
  "pakistanTime",
  "tomorrowMorning",
  "tomorrowEvening",
  "nextMonday",
  "selectDate",
  "selectTime",
  "cannotEditConfirmedSchedule",
  "cancel",
  "yesSchedule",
  "noChangeTime",
  "relatedQuestions",
  "selectYourList",
  "searchListsPlaceholder",
  "noListsMatchSearch",
  "createCustomList",
  "typing",
  "suggestedQuestionsHeader",
  "sentCannotEdit",
  "cannotRescheduleSentCampaign",
  "quickActionCreateCampaignTitle",
  "quickActionCreateCampaignSubtitle",
  "quickActionAnalyseScreenshotTitle",
  "quickActionAnalyseScreenshotSubtitle",
  "quickActionEngagementImpressionsTitle",
  "quickActionEngagementImpressionsSubtitle",
  "quickActionRoasTitle",
  "quickActionRoasSubtitle",
  "quickActionCreateListTitle",
  "quickActionCreateListSubtitle",
] as const;

type ChatScreenTranslationKey = typeof chatScreenTranslationKeys[number];
type ChatScreenText = Record<ChatScreenTranslationKey, string>;
type ChatScreenLocationState = {
  preserveSession?: boolean;
  liveChatState?: {
    query?: Message[];
    firstMessageSent?: boolean;
    suggestedQuestions?: string[];
    conversationStep?: any;
    campaignName?: string;
    audience?: string;
    selectedCampaign?: CampaignKey | null;
    selectedSubCampaign?: string | null;
    selectedCategories?: Category[];
    generatedHtml?: string | null;
    generatedPreviewUrl?: string | null;
    templateJSON?: any;
  };
};
const Chatbot = () => {
  const { t: translate, i18n } = useTranslation();
  useLanguage();
  const isArabic = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en"
  ).toLowerCase().startsWith("ar");
  const text: ChatScreenText = React.useMemo(
    () =>
      chatScreenTranslationKeys.reduce((acc, key) => {
        acc[key] = translate(`chatScreen.${key}`);
        return acc;
      }, {} as ChatScreenText),
    [translate, i18n.resolvedLanguage, i18n.language],
  );
  const chatbotRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [firstMessageSent, setFirstMessageSent] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [listCreationMode, setListCreationMode] = useState<
    "standalone" | "campaign" | null
  >(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isScheduledOrSent] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | File[] | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [conversationStep, setConversationStep] = useState<
    | "askName"
    | "impressionsStep"
    | "roasStep"
    | "conversionsStep"
    | "engagementStep"
    | "askAudience"
    | "askWebsite"
    | "fetchCategories"
    | "selectCategory"
    | "categoryStep"
    | "selectCampaign"
    | "selectSubCampaign"
    | "fillPrompt"
    | "customCampaignForm"
    | "confirmSend"
    | "sendOption"
    | "scheduleSend"
    | "schedulePicker"
    | "confirmCampaign"
    | "confirmation"
    | "autoFetchCategories"
    | null
  >(null);
  const clearedRef = useRef(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const [categorySearch, setCategorySearch] = useState("");
  const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
  const [generatedPreviewUrl, setGeneratedPreviewUrl] = useState<string | null>(
    null,
  );
  const [campaignName, setCampaignName] = useState("");
  const [audience, setAudience] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignKey | null>(
    null,
  );
  const user_id = localStorage.getItem("user_id");
  const tenant_id = localStorage.getItem("tenant_id") || "";
  const [isSendOptionDisabled, setIsSendOptionDisabled] = useState(false);
  const [isScheduleBoxDisabled, setIsScheduleBoxDisabled] = useState(false);
  const [persona, setPersona] = useState("");
  const [seasonalTag, setSeasonalTag] = useState("");
  const [offer, setOffer] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [, setPosition] = useState({
    x: window.innerWidth - 100,
    y: window.innerHeight - 100,
  });
  const dragging = useRef(false);
  const dragged = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const [selectedSubCampaign, setSelectedSubCampaign] = useState<string | null>(
    null,
  );
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [prompt, setPrompt] = useState("");
  const [selectedTones, setSelectedTones] = useState<string[]>([]);
  const [audiences, setAudiences] = useState<AudienceListOption[]>([]);
  const [audienceListSearch, setAudienceListSearch] = useState("");
  const isScheduledOrSentRef = useRef(isScheduledOrSent);
  const [, setInputDisabled] = useState(false);
  const [ctaLink, setCtaLink] = useState("");
  const navigate = useNavigate();
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);
  const [scheduledTime, setScheduledTime] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<any>(null);
  const [templateJSON, setTemplateJSON] = useState<any>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  /** `undefined` = not limited; string (from API `detail`) = show in thread + under input */
  const [queryLimitApiMessage, setQueryLimitApiMessage] = useState<string | undefined>(undefined);
  const location = useLocation();
  const locationState = location.state as ChatScreenLocationState | null;
  const consumedLiveStateRef = useRef(false);

  const getFileFromHistory = (message: any): Message["file"] | null => {
    const fileData =
      message?.file_data ||
      message?.fileData ||
      message?.file?.data ||
      message?.file?.file_data;
    const fileUrl =
      message?.image_url ||
      message?.file_url ||
      message?.fileUrl ||
      message?.file?.url ||
      message?.file?.file_url ||
      message?.file?.fileUrl;
    const url = fileData || fileUrl;

    if (!url) {
      return null;
    }

    const name =
      message?.file_name ||
      message?.fileName ||
      message?.file?.name ||
      "attachment";
    let type =
      message?.file_type || message?.fileType || message?.file?.type || "";

    if (message?.has_image && !type) {
      type = "image/jpeg";
    }

    if (!type && typeof url === "string" && url.startsWith("data:")) {
      type = url.split(";")[0].replace("data:", "");
    }

    // âœ… Handle chat-image endpoint URLs
    let finalUrl = url;
    if (typeof url === "string") {
      // Match patterns like /api/chat-image/115 or /api/chatbot/chat-image/115
      const chatImageMatch = url.match(
        /\/api\/(?:chatbot\/)?chat-image\/(\d+)/,
      );
      if (chatImageMatch) {
        const messageId = chatImageMatch[1];
        finalUrl = getChatImageUrl(messageId);
        if (!type) {type = "image/jpeg";} // Default to JPEG for chat images
      } else if (url.startsWith("/")) {
        // Handle other relative URLs
        const isDevelopment =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";
        const origin = API_ORIGIN;
        finalUrl = isDevelopment ? url : origin + url;
      }
    }

    return { name, type: type || "application/octet-stream", url: finalUrl };
  };

  const handleSelectChat = async (chat: ChatSession) => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {return;}

    setActiveChatSessionId(chat.session_id);
    setCurrentSessionId(chat.session_id);

    const data = await fetchChatMessages(userId, chat.session_id);

    const formatted: Message[] = data.flatMap((m: any) => {
      const userMsg: Message = {
        sender: "user",
        text: m.message,
        timestamp: m.timestamp ?? getTime(),
        type: "userMessage",
        file: getFileFromHistory(m),
      };

      const botMsg: Message = {
        sender: "bot",
        text: m.response?.content ?? "",
        timestamp: m.timestamp ?? getTime(),
        type: "botMessage",
      };

      return [userMsg, botMsg];
    });

    setQuery(formatted);
    syncChatbotMessageNumber(formatted.filter(msg => msg.sender === "user").length);

    setFirstMessageSent(true);
    setSuggestedQuestions([]);
    setConversationStep(null);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const filteredCategoriesForSearch = React.useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) {
      return categories;
    }
    return categories.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        (c.url && c.url.toLowerCase().includes(q)),
    );
  }, [categories, categorySearch]);

  const visibleCategories = React.useMemo(
    () => filteredCategoriesForSearch.slice(0, visibleCount),
    [filteredCategoriesForSearch, visibleCount],
  );

  const quickActions: {
    value: string;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
  }[] = [
    {
      value: "Create New Campaign",
      title: text.quickActionCreateCampaignTitle,
      subtitle: text.quickActionCreateCampaignSubtitle,
      icon: <Volume2 size={20} />,
    },
    {
      value: "Impressions",
      title: text.quickActionAnalyseScreenshotTitle,
      subtitle: text.quickActionAnalyseScreenshotSubtitle,
      icon: <ImagePlus size={20} />,
    },
    {
      value: "Engagement & Likes",
      title: text.quickActionEngagementImpressionsTitle,
      subtitle: text.quickActionEngagementImpressionsSubtitle,
      icon: <ChartLine size={20} />,
    },
    {
      value: "Return on Ad Spend",
      title: text.quickActionRoasTitle,
      subtitle: text.quickActionRoasSubtitle,
      icon: <CircleDollarSign size={20} />,
    },
    {
      value: "Create New List",
      title: text.quickActionCreateListTitle,
      subtitle: text.quickActionCreateListSubtitle,
      icon: <ListPlus size={20} />,
    },
  ];

  const filteredExistingAudiences = React.useMemo(
    () =>
      audiences
        .filter(a => a.name !== CREATE_NEW_LIST_NAME)
        .filter(a => audienceListMatchesSearch(a, audienceListSearch)),
    [audiences, audienceListSearch],
  );

  useEffect(() => {
    if (conversationStep !== "askAudience") {
      setAudienceListSearch("");
    }
  }, [conversationStep]);

  useEffect(() => {
    setVisibleCount(10);
  }, [categorySearch]);

  useEffect(() => {
    if (categories.length === 0) {
      setCategorySearch("");
    }
  }, [categories.length]);

  useEffect(() => {
    const hasStoredLiveState = Boolean(sessionStorage.getItem("ai_chat_live_state"));
    const hasLiveChatState =
      Boolean(locationState?.liveChatState?.query?.length) || hasStoredLiveState;
    const shouldPreserve = Boolean(locationState?.preserveSession) && !hasLiveChatState;

    if (!shouldPreserve) {
      resetChatSession();
    }
  }, [locationState]);

  useEffect(() => {
    if (consumedLiveStateRef.current) {return;}

    let liveChatState = locationState?.liveChatState;
    if (!liveChatState?.query?.length) {
      const stored = sessionStorage.getItem("ai_chat_live_state");
      if (stored) {
        try {
          liveChatState = JSON.parse(stored);
        } catch (error) {
          liveChatState = undefined;
        }
      }
    }

    if (!liveChatState?.query?.length) {return;}

    consumedLiveStateRef.current = true;
    sessionStorage.removeItem("ai_chat_live_state");
    resetChatSession();
    setCurrentSessionId(null);
    setQuery(liveChatState.query);
    setFirstMessageSent(
      liveChatState.firstMessageSent ?? liveChatState.query.length > 0,
    );
    setSuggestedQuestions(liveChatState.suggestedQuestions || []);
    setConversationStep(liveChatState.conversationStep ?? null);
    setCampaignName(liveChatState.campaignName || "");
    setAudience(liveChatState.audience || "");
    setSelectedCampaign(liveChatState.selectedCampaign || null);
    setSelectedSubCampaign(liveChatState.selectedSubCampaign || null);
    setSelectedCategories(liveChatState.selectedCategories || []);
    setGeneratedHtml(liveChatState.generatedHtml || null);
    setGeneratedPreviewUrl(liveChatState.generatedPreviewUrl || null);
    setTemplateJSON(liveChatState.templateJSON || null);
  }, [locationState]);

  useEffect(() => {
    const hasStoredLiveState = Boolean(sessionStorage.getItem("ai_chat_live_state"));
    const hasLiveChatState =
      Boolean(locationState?.liveChatState?.query?.length) || hasStoredLiveState;
    const shouldPreserve = Boolean(locationState?.preserveSession) && !hasLiveChatState;
    const sessionId = getActiveChatSessionId();
    const userId = localStorage.getItem("user_id");

    if (!shouldPreserve || !sessionId || !userId) {return;}

    const loadSession = async () => {
      try {
        const data = await fetchChatMessages(userId, sessionId);
        const formatted: Message[] = data.flatMap((m: any) => {
          const userMsg: Message = {
            sender: "user",
            text: m.message,
            timestamp: m.timestamp ?? getTime(),
            type: "userMessage",
            file: getFileFromHistory(m),
          };

          const botMsg: Message = {
            sender: "bot",
            text: m.response?.content ?? "",
            timestamp: m.timestamp ?? getTime(),
            type: "botMessage",
          };

          return [userMsg, botMsg];
        });

        setCurrentSessionId(sessionId);
        setQuery(formatted);
        syncChatbotMessageNumber(formatted.filter(msg => msg.sender === "user").length);
        setFirstMessageSent(true);
        setSuggestedQuestions([]);
        setConversationStep(null);
      } catch (error) {
        message.error(text.unableToLoadSession);
      }
    };

    loadSession();
  }, [locationState, text.unableToLoadSession]);

  useEffect(() => {
    scrollToBottom(messagesEndRef);
  }, [query, loading]);

  useEffect(() => {
    isScheduledOrSentRef.current = isScheduledOrSent;
  }, [isScheduledOrSent]);

  const apiLang = getApiLangFromI18n(i18n);

  useEffect(() => {
    const createNewListOption: AudienceListOption = {
      name: CREATE_NEW_LIST_NAME,
      displayName: text.createNewList,
    };

    const loadAudiences = async () => {
      try {
        const apiKey = localStorage.getItem("api_key") || "";
        const tenantId = localStorage.getItem("tenant_id") || "";

        if (!apiKey || !tenantId) {
          message.error(text.missingApiCredentials);
          return;
        }

        const response = await fetchCampaignListsAndCompany(tenantId, apiKey, apiLang);

        setAudiences(
          registerAudienceListLabels([
            ...toAudienceListOptions(response),
            createNewListOption,
          ]),
        );
      } catch (error) {
        const listOption = (key: string) =>
          translate(`audienceOverview.createSegment.listOptions.${key}`);
        setAudiences(
          registerAudienceListLabels([
            { name: "Loyal Customers", displayName: listOption("loyalCustomers") },
            { name: "High Spenders", displayName: listOption("highSpenders") },
            { name: "Cart Abandoners", displayName: listOption("cartAbandoners") },
            createNewListOption,
          ]),
        );
      }
    };

    loadAudiences();
  }, [apiLang, translate, text.missingApiCredentials, text.createNewList]);

  // "New Chat" starts a fresh session in the UI without deleting anything on the
  // server. Deleting a chat permanently is done from the history sidebar menu.
  const startNewChat = () => {
    clearedRef.current = true;
    resetChatSession();
    setCurrentSessionId(null);
    setQuery([]);
    setFirstMessageSent(false);
    setConversationStep(null);
    setSuggestedQuestions([]);
    setIsFetching(false);
    setLoading(false);
    setMenuOpen(false);
    setQueryLimitApiMessage(undefined);
  };

  const handleNewChat = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    startNewChat();
  };

  // Called by the sidebar after a chat is deleted server-side. If the deleted
  // chat is the one currently open, reset the screen to a fresh new chat.
  const handleChatDeleted = (deletedSessionId: string) => {
    if (deletedSessionId === currentSessionId) {
      startNewChat();
    }
  };

  const handleAutoCorrectChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (val: string) => void,
  ) => {
    const value = e.target.value;
    const lastChar = value.slice(-1);

    if ([" ", ".", ",", "!", "?"].includes(lastChar)) {
      setter(autoCorrectInput(value));
    } else {
      setter(value);
    }
  };

  const handleCancelClick = () => {
    if (isScheduledOrSentRef.current) {
      message.warning(
        text.sentCannotEdit,
      );
      return;
    }

    handleCancelSchedule(setScheduledDate, setScheduledTime, setQuery);
  };

  const triggerAutoFetchCategories = async () => {
    setLoading(true);
    setIsFetching(true);

    try {
      const response = await fetchCategories(getApiLangFromI18n(i18n));

      const fetchedCategories: Category[] =
        response?.website_categories?.map((url: string) => {
          const parts = url.split("/");
          const name = parts[parts.length - 1] || url;
          return { name, url };
        }) || [];

      setCategories(fetchedCategories);

      if (fetchedCategories.length > 0) {
        const categoryMessage: Message = {
          sender: "bot",
          timestamp: getTime(),
          type: "categoryStep",
          categories: fetchedCategories,
        };

        setQuery(prev => [...prev, categoryMessage]);
        setConversationStep("selectCategory");
      } else {
        const botMessage: Message = {
          sender: "bot",
          text: text.noCategoriesDetected,
          timestamp: getTime(),
        };
        setQuery(prev => [...prev, botMessage]);
        setConversationStep(null);
      }
    } catch (err) {
      const botMessage: Message = {
        sender: "bot",
        text: text.failedToFetchCategories,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
      setConversationStep(null);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const formatCategoryName = (name: string) => {
    return name.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleQueryLimitReached = React.useCallback((backendDetail: string) => {
    const messageFromApi = backendDetail.trim();
    setQueryLimitApiMessage(messageFromApi);
    if (backendDetail) {
      console.warn("Chatbot query limit reached:", backendDetail);
    }
    setQuery(prev => {
      const hasNotice = prev.some(msg => msg.type === "limitNotice");
      if (hasNotice) {
        return prev;
      }
      if (!messageFromApi) {
        return prev;
      }
      return [
        ...prev,
        {
          sender: "bot",
          type: "limitNotice",
          text: messageFromApi,
          timestamp: getTime(),
        },
      ];
    });
    setLoading(false);
    setIsFetching(false);
    setSuggestedQuestions([]);
  }, []);

  const messageHandler = React.useMemo(
    () =>
      createMessageHandler({
        textToSend: input,
        conversationStep,
        input,
        selectedFile,
        previewURL,
        campaignName,
        audience,
        generatedHtml,
        generatedPreviewUrl,
        listCreationMode,
        user_id,
        tenant_id,
        clearedRef,
        navigate,
        setQuery,
        setInput,
        setConversationStep,
        setCampaignName,
        setAudience,
        setListCreationMode,
        setLoading,
        setIsFetching,
        setFirstMessageSent,
        setSuggestedQuestions,
        setSelectedCategories,
        setSelectedCampaign,
        setSelectedSubCampaign,
        setCategories,
        triggerAutoFetchCategories,
        templateJSON,
        setTemplateJSON,
        onQueryLimitReached: handleQueryLimitReached,
      }),
    [
      input,
      conversationStep,
      selectedFile,
      previewURL,
      campaignName,
      audience,
      generatedHtml,
      generatedPreviewUrl,
      listCreationMode,
      user_id,
      tenant_id,
      navigate,
      setQuery,
      setInput,
      setConversationStep,
      setCampaignName,
      setAudience,
      setListCreationMode,
      setLoading,
      setIsFetching,
      setFirstMessageSent,
      setSuggestedQuestions,
      setSelectedCategories,
      setSelectedCampaign,
      setSelectedSubCampaign,
      setCategories,
      triggerAutoFetchCategories,
      handleQueryLimitReached,
      setSelectedCampaign,
      setSelectedSubCampaign,
      setCategories,
      triggerAutoFetchCategories,
      templateJSON,
    ],
  );

  const handleSuggestedQuestionClick = (question: string) => {
    if (question === "Return on Ad Spend") {
      const userMessage: Message = {
        sender: "user",
        text: text.returnOnAdSpend,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: text.roasReply,
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("roasStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }
    if (question === "Conversions") {
      const userMessage: Message = {
        sender: "user",
        text: text.conversions,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: text.conversionsReply,
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("conversionsStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }
    if (question === "Impressions") {
      const userMessage: Message = {
        sender: "user",
        text: text.impressions,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: text.impressionsReply,
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("impressionsStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }
    if (question === "Engagement & Likes" || question === "Engagements & Likes") {
      const userMessage: Message = {
        sender: "user",
        text: text.engagementAndLikes,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: text.engagementReply,
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("engagementStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }
    // "Create New Campaign" and "Create New List" are sent to the Ask DataTram
    // API like any other prompt. The backend classifies the message (via the
    // `intent` field) and the UI reacts to that, instead of a hardcoded script.
    setInput(question);
    setSuggestedQuestions([]);
    handleSendMessage(question);
  };

  const handleSendMessage = async (messageText?: string) => {
    if (queryLimitApiMessage !== undefined) {return;}
    const textToSend = messageText ?? input;
    const hasText = textToSend.trim().length > 0;
    const hasFile = !!selectedFile;
    if ((!hasText && !hasFile) || isFetching) {return;}

    // Clear Chat sets this to true to drop stale in-flight replies; reset so new sends map API text.
    clearedRef.current = false;

    if (!firstMessageSent) {setFirstMessageSent(true);}

    try {
      await messageHandler(hasText ? textToSend : "");
    } finally {
      // Always clear input and file regardless of success/failure
      setInput("");
      setSelectedFile(null);
      setPreviewURL(null);
    }
  };

  const createHtmlBlobUrl = (htmlString: string) => {
    const blob = new Blob([htmlString], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    return url;
  };

  const handleGenerate = async () => {
    setIsFetching(true);
    setLoading(true);

    const payload = {
      category_links: selectedCategories.map(cat => cat.url),
      campaign_type: selectedCampaign,
      campaign_subtype: selectedSubCampaign,
      prompt,
      tone: selectedTones.map(t => t.charAt(0).toUpperCase() + t.slice(1)),
      persona,
      seasonal_tag: seasonalTag,
      cta_label: ctaLabel,
      cta_url: ctaLink,
      tenant_id: Number(localStorage.getItem("tenant_id")),
      lang: getApiLangFromI18n(i18n),
      ...(offer ? { offer } : {}),
    };

    try {
      const res = await generateTemplate(payload);

      if (!res.success) {
        setQuery(prev => [
          ...prev,
          {
            sender: "bot",
            type: "text",
            text: res.errorMessage || text.failedToGenerateTemplate,
            timestamp: getTime(),
          },
        ]);
        return;
      }

      if (!res?.template && !res?.previewUrl)
        {throw new Error("No template returned");}
      if (res?.template) {setGeneratedHtml(res.template);}
      if (res?.design) {setTemplateJSON(res.design);}

      const previewUrl =
        res?.previewUrl ||
        (res?.template ? createHtmlBlobUrl(res.template) : null);
      if (!previewUrl) {throw new Error("No preview URL available");}
      setGeneratedPreviewUrl(previewUrl);

      setQuery(prev => [
        ...prev.filter(msg => msg.type !== "linkPreview"),
        {
          sender: "bot",
          type: "linkPreview",
          url: previewUrl,
          timestamp: getTime(),
        },
        {
          sender: "bot",
          type: "confirmSend",
          timestamp: getTime(),
        },
      ]);

      setConversationStep("confirmSend");
    } catch (err) {
      message.error(text.templateGenerationError);
      setQuery(prev => [
        ...prev,
        {
          sender: "bot",
          type: "text",
          text: text.failedToGenerateTemplate,
          timestamp: getTime(),
        },
      ]);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleCategorySelect = (category: Category) => {
    setSelectedCategories(prev => {
      const exists = prev.some(c => c.url === category.url);

      return exists
        ? prev.filter(c => c.url !== category.url)
        : [...prev, category];
    });
  };

  const handleCategoryProceed = () => {
    if (selectedCategories.length === 0) {
      const botMessage: Message = {
        sender: "bot",
        text: text.noCategoriesSelected,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
    } else {
      const botMessage: Message = {
        sender: "bot",
        text: `${text.selectedCategories} ${selectedCategories
          .map(cat => formatCategoryName(cat.name))
          .join(", ")}`,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
    }

    const campaignMessage: Message = {
      sender: "bot",
      text: text.selectCampaignCategory,
      timestamp: getTime(),
      type: "campaignOptions",
      options: campaigns,
    };
    setQuery(prev => [...prev, campaignMessage]);
    setConversationStep("selectCampaign");
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging.current || e.buttons === 0) {return;}
    dragged.current = true;
    setPosition({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y,
    });
  };

  const handleMouseUp = () => {
    dragging.current = false;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleCampaignConfirmation = (option: string) => {
    const userMessage: Message = {
      sender: "user",
      text: option === "Yes" ? text.yes : option === "No" ? text.no : option,
      timestamp: getTime(),
    };
    setQuery(prev => [...prev, userMessage]);

    if (option === "Yes") {
      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: text.createCampaignReply,
        timestamp: getTime(),
        type: "botQuestion",
      };

      setQuery(prev => [...prev, botMessage]);
      setConversationStep("askName");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
    } else {
      const botMessage: Message = {
        sender: "bot",
        text: text.okayNoProblem,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
      setConversationStep(null);
      setSuggestedQuestions([
        "Create New Campaign",
        "Show campaign performance",
        "View reports",
      ]);
      setInputDisabled(false);
    }
  };

  const WaveIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 35 32"
      fill="none"
    >
      <path
        d="M1 30.5C12.2113 27.974 5.52192 2.0716 17 1.5C28.5918 0.922731 22.2352 27.7056 33.5 30.5"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <div
      ref={chatbotRef}
      className="chatbot-screen-container"
      onClick={() => menuOpen && setMenuOpen(false)}
    >
      <div
        className={`chat-layout-container ${
          isSidebarOpen ? "sidebar-open" : ""
        }`}
      >
        {/* LEFT SIDEBAR */}
        <ChatHistorySidebar
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
          onSelectChat={handleSelectChat}
          onChatDeleted={handleChatDeleted}
          currentSessionId={currentSessionId}
        />

        <div className="chatbox-screen scrollable-chat chat-layout">
          <div className="chatbot-header blue-border">
            <div className="chatbot-header-left">
              <button
                className={`sidebar-btn ${isSidebarOpen ? "open hidden" : ""}`}
                onClick={() => setIsSidebarOpen(prev => !prev)}
                aria-label={text.toggleSidebar}
              >
                <img src="/Icons/sidebar-arrow.svg" alt="" />
              </button>
            </div>

            <div className="chatbox-header-actions">
              <button
                onClick={handleNewChat}
                className="button button--primary new-chat-btn"
              >
                {text.newChat}
              </button>
            </div>
          </div>
          <div
            className={`conversation-area-chatbot ${
              !firstMessageSent && conversationStep === null
                ? "without-suggestions"
                : ""
            }`}
          >
            {!firstMessageSent && conversationStep === null && (
              <>
                <div className="datatram-welcome-container">
                  <div className="datatram-avatar">
                    <Avatar size={35} icon={<WaveIcon />} />
                  </div>

                  <div className="datatram-greeting-title">
                    {text.greetingTitle}
                  </div>
                  <div className="datatram-greeting-subtitle">
                    {text.greetingSubtitle}
                  </div>
                </div>

                <div className="datatram-quick-actions">
                  {quickActions.map((action, idx: number) => (
                    <button
                      key={idx}
                      className="datatram-action-btn"
                      onClick={() => handleSuggestedQuestionClick(action.value)}
                    >
                      <span className="datatram-action-icon">{action.icon}</span>
                      <span className="datatram-action-content">
                        <span className="datatram-action-title">{action.title}</span>
                        <span className="datatram-action-subtitle">{action.subtitle}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div
              className={`chat-messages ${
                !firstMessageSent && conversationStep === null && query.length === 0
                  ? "empty"
                  : ""
              }`}
            >
              {query.map((msg, index) => (
                <div key={index}>
                  {msg.type === "categoryStep" && categories.length > 0 && (
                    <div className="category-bubble-container">
                      <div className="chatbot-avatar">
                        <Avatar
                          size={35}
                          icon={<WaveIcon />}
                          alt="Chatbot Avatar"
                        />
                      </div>
                      <div className="category-selection-container">
                        <p className="message-text">
                          {text.fetchedCategoriesPrompt}
                        </p>
                        {categorySearch.trim() &&
                          filteredCategoriesForSearch.length === 0 && (
                            <p className="category-search-empty" role="status">
                              {text.noCategoriesMatchSearch}
                            </p>
                          )}
                        <div className="category-grid category-grid-with-search">
                          <div
                            className="category-search-cell"
                            dir={isArabic ? "rtl" : "ltr"}
                          >
                            <Search
                              size={12}
                              className="category-search-icon"
                              aria-hidden
                            />
                            <input
                              type="search"
                              className="category-search-input"
                              value={categorySearch}
                              onChange={e => setCategorySearch(e.target.value)}
                              placeholder={text.searchCategoriesPlaceholder}
                              autoComplete="off"
                              spellCheck={false}
                              aria-label={text.searchCategoriesPlaceholder}
                            />
                          </div>
                          {visibleCategories.map((category, idx) => (
                            <div
                              key={category.url || `${category.name}-${idx}`}
                              className={`category-item ${
                                selectedCategories.some(
                                  c => c.url === category.url,
                                )
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => handleCategorySelect(category)}
                            >
                              <span className="category-name">
                                {formatCategoryName(category.name)}
                              </span>
                            </div>
                          ))}
                        </div>
                        {visibleCount < filteredCategoriesForSearch.length && (
                          <button
                            className="show-more-button"
                            onClick={() => setVisibleCount(visibleCount + 10)}
                          >
                            {text.showMoreCategories} <DownOutlined></DownOutlined>
                          </button>
                        )}
                        <div className="category-actions">
                          <button
                            className="proceed-button"
                            onClick={handleCategoryProceed}
                          >
                            {text.proceed}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Campaign options */}
                  {msg.type === "campaignOptions" && msg.options && (
                    <div className="campaign-screen-grid">
                      {msg.options.map(
                        (option: { title: string }, idx: number) => (
                          <button
                            key={idx}
                            className="campaign-option-button"
                            onClick={() =>
                              handleSendMessage(option.title || "")
                            }
                          >
                            {translateCampaignLabel(option.title)}
                            <span className="arrow">
                              <img src="/Icons/smallarrow.svg" alt="arrow" />
                            </span>
                          </button>
                        ),
                      )}
                    </div>
                  )}
                  {msg.type === "customCampaignForm" && (
                    <CustomCampaignForm
                      selectedCampaign={selectedCampaign}
                      setSelectedCampaign={setSelectedCampaign}
                      selectedSubCampaign={selectedSubCampaign}
                      setSelectedSubCampaign={setSelectedSubCampaign}
                      onAutoCorrectChange={handleAutoCorrectChange}
                      onProceed={(campaign, subCampaign) => {
                        const botMessage: Message = {
                          sender: "bot",
                          text: `${text.customCampaignCreated} ${campaign} → ${subCampaign}`,
                          timestamp: getTime(),
                          type: "botQuestion",
                        };
                        const promptMessage: Message = {
                          sender: "bot",
                          timestamp: getTime(),
                          type: "promptStep",
                        };
                        setQuery(prev => [
                          ...prev,
                          botMessage,
                          promptMessage,
                        ]);
                        setConversationStep("fillPrompt");
                      }}
                      setQuery={setQuery}
                    />
                  )}

                  {/* Sub-campaign options */}
                  {msg.type === "subCampaignOptions" && selectedCampaign && (
                    <div className="campaign-screen-grid">
                      {subCampaigns[selectedCampaign].map(
                        (
                          sub: { title: string; desc?: string },
                          idx: number,
                        ) => (
                          <button
                            key={idx}
                            className="campaign-option-button"
                            onClick={() => handleSendMessage(sub.title)}
                          >
                            <div>
                              <div className="subcampaign-title">
                                {translateCampaignLabel(sub.title)}
                              </div>
                            </div>
                            <span className="arrow">
                              <img src="/Icons/smallarrow.svg" alt="arrow" />
                            </span>
                          </button>
                        ),
                      )}
                    </div>
                  )}
                  {msg.type === "promptStep" && (
                    <PromptStep
                      prompt={prompt}
                      setPrompt={setPrompt}
                      selectedTones={selectedTones}
                      setSelectedTones={setSelectedTones}
                      persona={persona}
                      setPersona={setPersona}
                      seasonalTag={seasonalTag}
                      setSeasonalTag={setSeasonalTag}
                      offer={offer}
                      setOffer={setOffer}
                      ctaLabel={ctaLabel}
                      setCtaLabel={setCtaLabel}
                      ctaLink={ctaLink}
                      setCtaLink={setCtaLink}
                      onAutoCorrectChange={handleAutoCorrectChange}
                      onGenerate={handleGenerate}
                      loading={loading}
                    />
                  )}
                  {msg.type === "linkPreview" && msg.url && (
                    <div className="message-row bot">
                      <div className="chatbot-avatar">
                        <Avatar size={35} icon={<WaveIcon />} />
                      </div>
                      <div className="message-bubble bot-bubble">
                        <p>{text.generatedTemplate}</p>
                        <a
                          href={msg.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {text.previewTemplate}
                        </a>
                      </div>
                    </div>
                  )}

                  {msg.type === "confirmCampaign" && (
                    <div className="confirm-buttons">
                      {msg.options?.map((option: string, index: number) => (
                        <button
                          key={index}
                          className={`button ${
                            option === "Yes"
                              ? "button button--primary"
                              : "button button--outline"
                          }`}
                          onClick={() => handleCampaignConfirmation(option)}
                        >
                          {option === "Yes"
                            ? text.yes
                            : option === "No"
                              ? text.no
                              : option}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Step 1: Ask if ready to send */}
                  {msg.type === "confirmSend" && (
                    <div className="message-row bot">
                      <div className="chatbot-avatar">
                        <Avatar size={35} icon={<WaveIcon />} />
                      </div>
                      <div className="message-bubble bot-bubble">
                        <p>{text.readyToSend}</p>
                        <div className="template-action-buttons">
                          <button
                            className="template-btn primary"
                            onClick={() => handleSendMessage("Yes")}
                          >
                            {text.yes}
                          </button>
                          <button
                            className="template-btn secondary"
                            onClick={() => handleSendMessage("Edit")}
                          >
                            {text.edit}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Choose send option */}
                  {msg.type === "sendOption" && (
                    <div className="message-row bot">
                      <div className="chatbot-avatar">
                        <Avatar size={35} icon={<WaveIcon />} />
                      </div>
                      <div className="message-bubble bot-bubble">
                        <p>{text.sendOrSchedule}</p>
                        <div className="template-action-buttons">
                          <button
                            className="template-btn primary"
                            onClick={() => {
                              if (isSendOptionDisabled) {
                                message.warning(text.alreadySentWarning);
                                return;
                              }
                              handleSendMessage("Send Now");
                              setIsSendOptionDisabled(true);
                              setIsScheduleBoxDisabled(true);
                            }}
                            disabled={isSendOptionDisabled}
                          >
                            {text.sendNow}
                          </button>

                          <button
                            className="template-btn secondary"
                            onClick={() => {
                              if (isSendOptionDisabled) {
                                message.warning(text.alreadySentScheduleWarning);
                                return;
                              }
                              handleSendMessage("Schedule");
                              setIsSendOptionDisabled(true);
                            }}
                            disabled={isSendOptionDisabled}
                          >
                            {text.schedule}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {msg.type === "schedulePicker" && (
                    <div
                      className={`schedule-picker-box ${
                        isScheduleBoxDisabled ? "disabled" : ""
                      }`}
                    >
                      <h4 className="schedule-picker-title">
                        {" "}
                        {text.scheduleCampaign}
                      </h4>
                      <p className="schedule-picker-paragraph">
                        {text.pakistanTime}
                      </p>

                      {/* Preset quick scheduling options */}
                      <div className="schedule-picker-presets">
                        {(() => {
                          const today = new Date();
                          const tomorrow = new Date(today);
                          tomorrow.setDate(today.getDate() + 1);

                          const nextMonday = new Date(today);
                          const dayOffset = (8 - today.getDay()) % 7 || 7;
                          nextMonday.setDate(today.getDate() + dayOffset);

                          const formatDay = (d: Date) =>
                            d.toLocaleDateString(isArabic ? "ar-SA" : "en-US", {
                              weekday: "long",
                            });

                          const presets = [
                            {
                              label: `${text.tomorrowMorning} (9:00 AM - ${formatDay(
                                tomorrow,
                              )})`,
                              time: "09:00",
                            },
                            {
                              label: `${text.tomorrowEvening} (6:00 PM - ${formatDay(
                                tomorrow,
                              )})`,
                              time: "18:00",
                            },
                            {
                              label: `${text.nextMonday} 9:00 AM (${formatDay(
                                nextMonday,
                              )})`,
                              time: "09:00",
                              dayOffset,
                            },
                          ];

                          return presets.map((preset, index) => (
                            <button
                              key={index}
                              className="preset-button"
                              onClick={() => {
                                if (isScheduledOrSent) {
                                  message.warning(
                                    text.cannotRescheduleSentCampaign,
                                  );
                                  return;
                                }
                                handleQuickSchedule(
                                  preset,
                                  setSelectedPreset,
                                  setQuery,
                                );
                              }}
                              disabled={isScheduledOrSent}
                            >
                              {preset.label}
                            </button>
                          ));
                        })()}
                      </div>

                      {/* Manual date & time inputs */}
                      <label className="schedule-label">{text.selectDate}</label>
                      <input
                        type="date"
                        className="schedule-input"
                        min={new Date().toISOString().split("T")[0]}
                        onChange={e => setScheduledDate(e.target.value)}
                        disabled={isScheduledOrSent}
                      />

                      <label className="schedule-label">{text.selectTime}</label>
                      <input
                        type="time"
                        className="schedule-input"
                        onChange={e => setScheduledTime(e.target.value)}
                        disabled={isScheduledOrSent}
                      />

                      <div className="schedule-actions">
                        <button
                          className="proceed-button"
                          onClick={() => {
                            if (isScheduleBoxDisabled) {
                              message.warning(text.cannotEditConfirmedSchedule);
                              return;
                            }
                            handleConfirmSchedule(
                              scheduledDate,
                              scheduledTime,
                              setQuery,
                            );
                            setIsScheduleBoxDisabled(true);
                          }}
                          disabled={
                            !scheduledDate ||
                            !scheduledTime ||
                            isScheduleBoxDisabled
                          }
                        >
                          {text.schedule}
                        </button>

                        <button
                          className="proceed-button"
                          onClick={handleCancelClick}
                        >
                          {text.cancel}
                        </button>
                      </div>
                    </div>
                  )}

                  {msg?.type === "confirmation" && (
                    <div className="bot-message confirmation">
                      <div className="confirmation-buttons">
                        <button
                          className="yes-button"
                          onClick={() =>
                            handleScheduleConfirmation(
                              "yes",
                              selectedPreset,
                              setScheduledDate,
                              setScheduledTime,
                              setQuery,
                            )
                          }
                        >
                          {text.yesSchedule}
                        </button>
                        <button
                          className="no-button"
                          onClick={() =>
                            handleScheduleConfirmation(
                              "no",
                              selectedPreset,
                              setScheduledDate,
                              setScheduledTime,
                              setQuery,
                            )
                          }
                        >
                          {text.noChangeTime}
                        </button>
                      </div>
                    </div>
                  )}

                  {msg.type === "menuQuestions" && msg.menu && (
                    <div className="message bot">
                      <div className="message-text">
                        {text.relatedQuestions}
                      </div>
                      <div className="menu-questions-grid">
                        {msg.menu?.questions?.map((q, idx) => (
                          <button
                            key={idx}
                            className="campaign-option-button"
                            onClick={() => handleSendMessage(q.text)}
                          >
                            <img
                              src={q.icon}
                              alt="q-icon"
                              className="menu-question-icon"
                            />
                            <span>{q.text}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {msg.type === "limitNotice" && msg.text && (
                    <div className="message-row bot system-notice-row">
                      <div className="chatbot-avatar">
                        <Avatar size={35} icon={<WaveIcon />} />
                      </div>
                      <div className="message-bubble system-notice-bubble">
                        {msg.text}
                      </div>
                    </div>
                  )}

                  {/* Default message rendering - shows IMAGE FIRST then TEXT */}
                  {!msg.type ||
                  msg.type === "userMessage" ||
                  msg.type === "botMessage" ||
                  msg.type === "botQuestion" ? (
                    <>
                      {/* SHOW IMAGE FIRST if it exists */}
                      {msg.file &&
                        msg.file.url &&
                        msg.file?.type?.startsWith("image/") && (
                          <div className={`message-row ${msg.sender}`}>
                            {msg.sender === "bot" && (
                              <div className="chatbot-avatar">
                                <Avatar size={35} icon={<WaveIcon />} />
                              </div>
                            )}
                            <div
                              className={`message-bubble ${
                                msg.sender === "bot"
                                  ? "bot-bubble"
                                  : "user-bubble"
                              }`}
                              style={{
                                background: "transparent",
                                padding: msg.text ? "0" : "0",
                                boxShadow: "none",
                              }}
                            >
                              <img
                                src={msg.file.url}
                                alt={msg.file.name}
                                className="chat-image-preview"
                                onError={e => {
                                  console.error(
                                    "Failed to load image:",
                                    msg.file?.url,
                                  );
                                  e.currentTarget.style.display = "none";
                                }}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "400px",
                                  borderRadius: "8px",
                                  marginBottom: msg.text ? "12px" : "0",
                                  display: "block",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          </div>
                        )}

                      {/* SHOW TEXT AFTER IMAGE */}
                      {msg.text && (
                        <div
                          className={`message-row ${msg.sender} ${
                            msg.type === "botQuestion" ? "bot-question" : ""
                          }`}
                        >
                          {msg.sender === "bot" && !msg.file && (
                            <div className="chatbot-avatar">
                              <Avatar size={35} icon={<WaveIcon />} />
                            </div>
                          )}

                          <div
                            className={`message-bubble ${
                              msg.sender === "bot"
                                ? "bot-bubble"
                                : "user-bubble"
                            }`}
                          >
                            {msg.sender === "bot" ? (
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {msg.text}
                              </ReactMarkdown>
                            ) : (
                              msg.text
                            )}
                          </div>
                        </div>
                      )}

                      {/* SHOW NON-IMAGE FILE LINK */}
                      {msg.file && !msg.file?.type?.startsWith("image/") && (
                        <div className={`message-row ${msg.sender}`}>
                          {msg.sender === "bot" && !msg.text && (
                            <div className="chatbot-avatar">
                              <Avatar size={35} icon={<WaveIcon />} />
                            </div>
                          )}
                          <div
                            className={`message-bubble ${
                              msg.sender === "bot"
                                ? "bot-bubble"
                                : "user-bubble"
                            }`}
                          >
                            <a
                              href={msg.file.url || undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="chat-file-link"
                            >
                              ðŸ“Ž {msg.file.name}
                            </a>
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              ))}
              {/* Show audience options ONLY at askAudience step */}

              {/* Audience options ONLY at askAudience step */}
              {conversationStep === "askAudience" && (
                <div className="audience-selection-container">
                  {Array.isArray(audiences) && audiences.length > 0 && (
                    <>
                      <div className="audience-list-heading">
                        {text.selectYourList}
                      </div>
                      {audienceListSearch.trim() &&
                        filteredExistingAudiences.length === 0 && (
                          <p className="audience-list-empty-filter" role="status">
                            {text.noListsMatchSearch}
                          </p>
                        )}
                      <div className="campaign-screen-grid audience-list-grid-with-search">
                        <div
                          className="audience-list-search-cell"
                          dir={isArabic ? "rtl" : "ltr"}
                        >
                          <Search
                            size={12}
                            className="audience-list-search-icon"
                            aria-hidden
                          />
                          <input
                            type="search"
                            className="audience-list-search-input"
                            value={audienceListSearch}
                            onChange={e => setAudienceListSearch(e.target.value)}
                            placeholder={text.searchListsPlaceholder}
                            autoComplete="off"
                            spellCheck={false}
                            aria-label={text.searchListsPlaceholder}
                          />
                        </div>
                        {filteredExistingAudiences.map((audience, idx) => (
                          <button
                            key={`${audience.name}-${idx}`}
                            className="campaign-option-button"
                            /* Send the canonical `name` — segment lookup matches on it, not the label. */
                            onClick={() => handleSendMessage(audience.name)}
                          >
                            {audience.displayName}
                            <span className="arrow">
                              <img src="/Icons/smallarrow.svg" alt="arrow" />
                            </span>
                          </button>
                        ))}
                      </div>
                      {/* TEMP: "Create New List" hidden in the campaign audience step (chatbot flow only). Re-enable by changing `false` back to `true`. */}
                      {false && (
                        <>
                          <div className="custom-list-heading">
                            {text.createCustomList}
                          </div>
                          <div className="campaign-screen-grid">
                            {audiences
                              .filter(aud => aud.name === CREATE_NEW_LIST_NAME)
                              .map((audience, idx) => (
                                <button
                                  key={idx}
                                  className="campaign-option-button"
                                  onClick={() => handleSendMessage(audience.name)}
                                >
                                  {audience.displayName}
                                  <span className="arrow">
                                    <img src="/Icons/smallarrow.svg" alt="arrow" />
                                  </span>
                                </button>
                              ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {loading && (
                <div className="message-row bot typing-message">
                  <div className="chatbot-avatar">
                    <Avatar
                      size={35}
                      icon={<WaveIcon />}
                      alt="Chatbot Avatar"
                    />
                  </div>
                  <div className="message-bubble bot-bubble typing-bubble">
                    <span className="typing-text">{text.typing}</span>
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {suggestedQuestions.length > 0 && (
            <div className="compact-suggestions-container">
              <div className="compact-suggestions-header">
                {text.suggestedQuestionsHeader}
              </div>
              <div className="compact-suggestions-scroll">
                {suggestedQuestions.map((question, index) => (
                  <button
                    className="compact-pill-button"
                    key={index}
                    onClick={() => handleSuggestedQuestionClick(question)}
                  >
                    {question === "Create New Campaign"
                      ? text.createNewCampaign
                      : question === "Create New List"
                        ? text.quickActionCreateListTitle
                        : question === "Show campaign performance"
                          ? text.showCampaignPerformance
                          : question === "View reports"
                            ? text.viewReports
                            : question === "Return on Ad Spend"
                              ? text.returnOnAdSpend
                              : question === "Engagement & Likes" ||
                                  question === "Engagements & Likes"
                                ? text.engagementAndLikes
                                : question === "Conversions"
                                  ? text.conversions
                                  : question === "Impressions"
                                    ? text.impressions
                                    : question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* âŒ¨ï¸ Input */}
          <div className="chat-screen-input-shell">
            <ChatInput
              input={input}
              setInput={setInput}
              handleSendMessage={handleSendMessage}
              isFetching={isFetching}
              isInputLocked={queryLimitApiMessage !== undefined}
              inputHelperText={queryLimitApiMessage || undefined}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              previewURL={previewURL}
              setPreviewURL={setPreviewURL}
              showAttachmentHints
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
